import type React from "react";
import { useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Label,
  Tooltip,
} from "recharts";
import TimeRangeDropdown from "../../time-range-filter/time-range-dropdown";
import "./revenue-overview.css";
import SimpleSpinner from "../../../components/loader";
import EmptyState from "../../../newComponents/emptystate/index";
import { GetImages } from "../../../utils/GetImages";
import CurrencyTooltip from "../../../components/currencytooltip/CurrencyTooltip";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";

interface OverviewData {
  title: string;
  value: any;
  color: string;
  formattedValue?: string;
  valueRaw?: string;
}

interface RevenueOverviewProps {
  totalName?: string;
  heading?: string;
  data: OverviewData[];
  valueTotal?: string | number;
  valueTotalRaw?: string;
  timeRanges?: string[];
  onTimeRangeChange?: (newRange: string) => void;
  dateFilter?: string;
  valueSymbol?: string;
  symbolPosition?: "before" | "after";
  alertStripText?: string;
  alertStripSubText?: string;
  isAlertStrip?: boolean;
  isLoading?: boolean;
  formatAmount?: boolean;
  isCurrency?: boolean;
}

const DonutChart: React.FC<RevenueOverviewProps> = ({
  totalName = "Total",
  heading = "Overview",
  data,
  valueTotal,
  valueTotalRaw,
  timeRanges = ["Last 12 Month", "Last 6 Month", "Last 3 Month", "This Month"],
  onTimeRangeChange,
  dateFilter = "Last 12 Month",
  valueSymbol = "$",
  symbolPosition = "before",
  alertStripText,
  alertStripSubText,
  isAlertStrip = false,
  isLoading = false,
  formatAmount=true,
  isCurrency=true
}) => {
  const currencyFromHook = useCurrencySymbol();
  const effectiveSymbol = isCurrency ? currencyFromHook : valueSymbol;

  const [selectedRange, setSelectedRange] = useState(dateFilter);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

   const handleMouseMove = (e) => {
    if (e.activePayload && e.activePayload.length) {
      // Access coordinates of the active point from the event object
      setTooltipPosition({ x: e.chartX - 20, y: e.chartY - 60  }); // Offset for better placement
    }
  };

  const handleTimeRangeChange = (newRange: string) => {
    setSelectedRange(newRange);
    onTimeRangeChange?.(newRange);
  };

  const formatValue = (amount: number) => {
    if (effectiveSymbol === "$" || effectiveSymbol === "USD") {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(amount);
    }

    return symbolPosition === "before"
      ? `${effectiveSymbol}${amount}`
      : `${amount}${effectiveSymbol}`;
  };

  const formatCurrency = (value: number) => {
    if (isNaN(value)) return `${effectiveSymbol}0.00`;

    const suffixes = ["", "K", "M", "B", "T", "Q", "Qu", "Sx", "Sp", "Oc", "N"];
    let num = Math.abs(value);
    let i = 0;

    while (num >= 1000 && i < suffixes.length - 1) {
      num /= 1000;
      i++;
    }

    const formattedNumber =
      num < 1000
        ? num.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : num % 1 === 0
        ? num.toLocaleString("en-US", { maximumFractionDigits: 0 })
        : num.toLocaleString("en-US", {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          });

    const result = `${formattedNumber}${suffixes[i]}`;
    if (value < 0) {
      return symbolPosition === "before"
        ? `(${effectiveSymbol}${result})`
        : `(${result}${effectiveSymbol})`;
    }
    return symbolPosition === "before"
      ? `${effectiveSymbol}${result}`
      : `${result}${effectiveSymbol}`;
  };

  const formatCurrencyForNegative = (value: number) => {
    if (isNaN(value)) return `${effectiveSymbol}0.00`;

    const suffixes = ["", "K", "M", "B", "T", "Q", "Qu", "Sx", "Sp", "Oc", "N"];
    let num = Math.abs(value);
    let i = 0;

    while (num >= 1000 && i < suffixes.length - 1) {
      num /= 1000;
      i++;
    }

    const formattedNumber =
      num < 1000
        ? num.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : num % 1 === 0
        ? num.toLocaleString("en-US", { maximumFractionDigits: 0 })
        : num.toLocaleString("en-US", {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          });

    const result = `${formattedNumber}${suffixes[i]}`;
    if (value < 0) {
      return symbolPosition === "before"
        ? `-${effectiveSymbol}${result}`
        : `-${result}${effectiveSymbol}`;
    }
    return symbolPosition === "before"
      ? `${effectiveSymbol}${result}`
      : `${result}${effectiveSymbol}`;
  };

  const sanitizeData = (data: OverviewData[]) => {
    // Store original values and use absolute values for chart display
    const cleaned = data.map((d) => ({
      ...d,
      originalValue: Number(d.value) || 0,
      value: Math.abs(Number(d.value) || 0), // Use absolute value for chart
    }));

    const total = cleaned.reduce((sum, d) => sum + d.value, 0);
    if (total === 0) return cleaned;

    // Calculate percentages based on absolute values
    return cleaned.map((d) => ({
      ...d,
      value: (d.value / total) * 100,
    }));
  };

  const chartData = sanitizeData(data);
  const total = valueTotal ?? 0;
  const totalRaw = valueTotalRaw ?? 0;
  const hasData = data.length > 0 && chartData.some(d => d.value > 0);

  
   const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: any;
}) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;
      const originalValue = d.originalValue;

    return (
      <div className="chart-tooltip">
        <div className="tooltip-content">
          <span style={{ color: d.color, fontWeight: 'bold' }}>{d.title}: </span>
          {d.formattedValue ? (
            formatAmount ? (
              d.formattedValue.includes(effectiveSymbol) ? d.formattedValue : (
                symbolPosition === "before" ? `${effectiveSymbol}${d.formattedValue}` : `${d.formattedValue}${effectiveSymbol}`
              )
            ) : (
              d.formattedValue
            )
          ) : (
             formatAmount ? (
                 originalValue < 0
                  ? formatCurrencyForNegative(originalValue)
                  : formatCurrency(originalValue)
             ) : (
                originalValue
             )
          )}
        </div>
        <div className="tooltip-arrow"></div>
      </div>
    );
  }

  return null;
};




  return (
    <div className="revenue-overview">
      <div className="revenue-overview-header">
        <h2 className="revenue-overview-title">{heading}</h2>
        <TimeRangeDropdown
          timeRanges={timeRanges}
          defaultTimeRange={selectedRange}
          onTimeRangeChange={handleTimeRangeChange}
          className="my-custom-dropdown"
        />
      </div>

      <div className="revenue-overview-content">
        {isLoading ? (
          <div className="loading-spinner">
            <SimpleSpinner myClassName={"Icon50 mt-5"} />
          </div>
        ) : hasData ? (
          <>
            <div className="gauge-chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart onMouseMove={handleMouseMove}>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="title"
                    cx="50%"
                    cy="70%"
                    innerRadius={85}
                    outerRadius={105}
                    startAngle={180}
                    endAngle={0}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                    <Label
                      content={({ viewBox }) => {
                        if (!viewBox || !("cx" in viewBox)) return null;
                        return (
                          isCurrency ? <g>
                            <CurrencyTooltip
                              content={totalRaw}
                              formatted={total}
                            >
                              <text
                                x={viewBox.cx}
                                y={viewBox.cy - 45}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                className="total-revenue-value"
                              >
                                {`[[currency]]${total}`}
                              </text>
                            </CurrencyTooltip>
                            <text
                              x={viewBox.cx}
                              y={viewBox.cy - 15}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              className="total-revenue-label"
                            >
                              {totalName}
                            </text>
                          </g> : <g>
                            <text
                              x={viewBox.cx}
                              y={viewBox.cy - 45}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              className="total-revenue-value"
                            >
                               {symbolPosition === "before" ? `${valueSymbol}${total}` : `${total}${valueSymbol}`}
                            </text>
                            <text
                              x={viewBox.cx}
                              y={viewBox.cy - 15}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              className="total-revenue-label"
                            >
                              {totalName}
                            </text>
                          </g>
                        );
                      }}
                    />
                  </Pie>
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "transparent" }}  position={tooltipPosition}  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="revenue-metrics">
              {data?.map((item, index) => (
                <div key={index} className="revenue-metric-item">
                  <div className="metric-label">
                    <span
                      className="metric-dot"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="metric-name">{item.title}</span>
                  </div>

                  <div className="metric-dashed-line"></div>

                  <div className="metric-value">
                    {isCurrency ? <CurrencyTooltip
                      content={item.valueRaw || item.value}
                      formatted={item.formattedValue}
                    >
                      <span>{`${item.formattedValue || item.value}`}</span>
                    </CurrencyTooltip> : <span>{item.formattedValue || item.value}</span>}
                  </div>
                </div>
              ))}
            </div>

            {isAlertStrip && alertStripText && alertStripSubText && (
              <div className="alert-strip">
                <span className="alert-text left">{alertStripText}</span>
                <span className="alert-text right">{alertStripSubText}</span>
              </div>
            )}
          </>
        ) : (
          <EmptyState
            icon={GetImages.emptyStateData}
            className="w-100"
            isBorder={false}
            heading="No Data found"
            isButton={false}
          />
        )}
      </div>
    </div>
  );
};

export default DonutChart;